import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import {
  Archive,
  CalendarArrowRight,
  Delete,
  EyeVisible,
  LayersFront,
  Pencil,
  Settings,
  SolidPlay,
  SolidStop,
} from '@factorialco/f0-react/icons/app'
import { type SurveyResourceHeaderSurveyFragment } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { ArchiveSurveyDialog } from 'modules/engagement_surveys/components/ArchiveSurveyDialog'
import { DeleteSurveyDialog } from 'modules/engagement_surveys/components/DeleteSurveyDialog'
import { DuplicateSurveyDialog } from 'modules/engagement_surveys/components/DuplicateSurveyDialog'
import { formatDate, formatDateTime } from 'modules/engagement_surveys/lib/dates'
import { formRecurrenceMapper } from 'modules/engagement_surveys/lib/formRecurrenceMapper'
import { formStatusMapper } from 'modules/engagement_surveys/lib/formStatusMapper'
import { FormRender } from 'modules/forms'

import { EditSurveyWizard } from './EditSurveyWizard'
import { FinishSurveyDialog } from './FinishSurveyDialog'
import { PublishDialog } from './PublishDialog'
import { ScheduleSettingsDialog } from './ScheduleSettingsDialog'

type Dialogs =
  | 'publish'
  | 'scheduleSettings'
  | 'finish'
  | 'archive'
  | 'delete'
  | 'duplicate'
  | 'surveySettings'

interface Props {
  survey: SurveyResourceHeaderSurveyFragment
  canCreateSurvey: boolean
}

export default function SurveyResourceHeader({ survey, canCreateSurvey }: Props) {
  const [previewOpen, setPreviewOpen] = useState(false)
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const form = survey.form
  const formStatus = form.status
  const status = formStatusMapper(formStatus)
  const [dialogToShow, setDialogToShow] = useState<Dialogs | null>(null)

  const primaryAction = (() => {
    if (!survey._policies?.edit.can) {
      return undefined
    }

    if (formStatus === 'draft') {
      return {
        label: i18n.t('common.publish'),
        icon: SolidPlay,
        onClick: () => setDialogToShow('publish'),
      }
    }

    if (formStatus === 'finished') {
      return {
        label: i18n.t('common.publish'),
        icon: SolidPlay,
        onClick: () => setDialogToShow('publish'),
      }
    }

    if (formStatus === 'scheduled') {
      return {
        label: i18n.t('surveys.header.actions.change_schedule'),
        icon: CalendarArrowRight,
        onClick: () => setDialogToShow('publish'),
      }
    }

    return undefined
  })()

  const otherActions = (() => {
    if (!survey._policies?.edit.can) {
      return undefined
    }

    const all = []

    if (formStatus !== 'archived') {
      all.push({
        key: 'survey-settings',
        label: i18n.t('surveys.header.actions.survey_settings'),
        icon: Settings,
        onClick: () => {
          setDialogToShow('surveySettings')
        },
      })
    }

    if (canCreateSurvey) {
      all.push({
        key: 'duplicate',
        label: i18n.t('common.duplicate'),
        icon: LayersFront,
        onClick: () => setDialogToShow('duplicate'),
      })
    }

    if (formStatus === 'finished') {
      all.push({
        key: 'archive',
        label: i18n.t('common.archive'),
        icon: Archive,
        critical: true,
        onClick: () => setDialogToShow('archive'),
      })
    }

    if (formStatus === 'draft') {
      all.push({
        key: 'delete',
        label: i18n.t('common.delete'),
        icon: Delete,
        onClick: () => setDialogToShow('delete'),
        critical: true,
      })
    }

    return all
  })()

  const secondaryActions = (() => {
    const all = []

    if (['draft', 'scheduled', 'active'].includes(formStatus)) {
      if (survey._policies?.edit.can) {
        all.push({
          key: 'edit-content',
          label: i18n.t('surveys.header.actions.edit_content'),
          icon: Pencil,
          onClick: () => {
            navigate(
              navigation.engagement.smart_surveys.survey.edit.fullPath({ surveyId: survey.id })
            )
          },
        })
      }

      all.push({
        key: 'preview',
        label: i18n.t('common.preview'),
        icon: EyeVisible,
        onClick: () => setPreviewOpen((prev) => !prev),
      })
    }

    if (formStatus === 'active' && survey._policies?.edit.can) {
      all.push({
        key: 'finish-survey',
        label: i18n.t('surveys.header.actions.finish_survey'),
        variant: 'critical' as const,
        icon: SolidStop,
        onClick: () => setDialogToShow('finish'),
      })
    }

    return all
  })()

  const latestCycle = form.schedule?.cyclesConnection?.nodes?.at(0)
  const surveyFinished = ['finished', 'archived'].includes(formStatus)
  const surveyArchived = formStatus === 'archived'

  const metadata = (() => {
    const all = []
    const hideActions = surveyArchived || !survey._policies?.edit.can

    if (latestCycle) {
      all.unshift({
        key: 'publish-date',
        label: i18n.t('surveys.header.metadata.publish_date'),
        value: {
          content: formatDateTime(latestCycle.startsAt),
          type: 'text' as const,
        },
        actions: hideActions
          ? []
          : [
              {
                icon: Pencil,
                label: i18n.t('surveys.header.metadata.publish_date_tooltip'),
                onClick: () => setDialogToShow('scheduleSettings'),
              },
            ],
      })
    }

    all.push({
      key: 'recurrence',
      label: i18n.t('surveys.header.metadata.recurrence'),
      value: {
        content: formRecurrenceMapper(form.schedule?.recurrenceType || 'onetime'),
        type: 'text' as const,
      },
      actions: hideActions
        ? []
        : [
            {
              icon: Pencil,
              label: i18n.t('surveys.header.metadata.recurrence_tooltip'),
              onClick: () => setDialogToShow('scheduleSettings'),
            },
          ],
    })

    if (form.schedule?.nextRunDate && !surveyFinished) {
      all.push({
        key: 'next-launch',
        label: i18n.t('surveys.header.metadata.next_launch'),
        value: { content: formatDateTime(form.schedule.nextRunDate), type: 'text' as const },
      })
    }

    if (!surveyFinished) {
      all.push({
        key: 'ends-at',
        label: i18n.t('surveys.header.metadata.ends_at'),
        value: {
          content: form.endsAt ? formatDate(form.endsAt) : 'Never ends',
          type: 'text' as const,
        },
        actions: hideActions
          ? []
          : [
              {
                icon: Pencil,
                label: i18n.t('surveys.header.metadata.ends_at_tooltip'),
                onClick: () => setDialogToShow('scheduleSettings'),
              },
            ],
      })
    }

    if (form.schedule?.recurrenceType !== 'onetime' && form.schedule?.recurrenceType !== 'daily') {
      all.push({
        key: 'days-to-answer',
        label: i18n.t('surveys.header.metadata.days_to_answer'),
        value: { content: form.schedule?.daysToAnswer?.toString() || '-', type: 'text' as const },
        actions: hideActions
          ? []
          : [
              {
                icon: Pencil,
                label: i18n.t('surveys.header.metadata.days_to_answer_tooltip'),
                onClick: () => setDialogToShow('scheduleSettings'),
              },
            ],
      })
    }

    return all
  })()

  return (
    <>
      <ResourceHeader
        title={form.title}
        status={{
          label: status.label,
          text: status.label,
          variant: status.status,
        }}
        metadata={metadata}
        primaryAction={primaryAction}
        secondaryActions={secondaryActions}
        otherActions={otherActions}
      />

      {previewOpen && (
        <FormRender
          mode="preview"
          formId={form.id}
          resourceType="Surveys::Survey"
          onClose={() => setPreviewOpen(false)}
          isOpen
          onSubmitSuccess={() => {}}
        />
      )}

      {dialogToShow === 'duplicate' && (
        <DuplicateSurveyDialog
          surveyId={survey.id}
          onSuccess={(newSurvey) => {
            setDialogToShow(null)
            navigate(
              navigation.engagement.smart_surveys.survey.fullPath({ surveyId: newSurvey.id })
            )
          }}
          onCancel={() => setDialogToShow(null)}
        />
      )}

      {dialogToShow === 'delete' && (
        <DeleteSurveyDialog
          surveyId={survey.id}
          onSuccess={() => {
            setDialogToShow(null)
            navigate(navigation.engagement.smart_surveys.fullPath())
          }}
          onCancel={() => setDialogToShow(null)}
        />
      )}

      {dialogToShow === 'finish' && (
        <FinishSurveyDialog
          formId={form.id}
          onSuccess={() => setDialogToShow(null)}
          onCancel={() => setDialogToShow(null)}
        />
      )}

      {dialogToShow === 'archive' && (
        <ArchiveSurveyDialog
          surveyId={survey.id}
          onSuccess={() => setDialogToShow(null)}
          onCancel={() => setDialogToShow(null)}
        />
      )}

      {dialogToShow === 'surveySettings' && (
        <EditSurveyWizard
          survey={survey}
          onClose={() => setDialogToShow(null)}
          onSuccess={() => setDialogToShow(null)}
        />
      )}

      {dialogToShow === 'publish' && (
        <PublishDialog survey={survey} onClose={() => setDialogToShow(null)} />
      )}

      {dialogToShow === 'scheduleSettings' && (
        <ScheduleSettingsDialog form={survey.form} onClose={() => setDialogToShow(null)} />
      )}
    </>
  )
}
